import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { PALETTE, SERIES_COLORS, fmtDays, fmtPct, fmtNumber } from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * ProfileCompareBar — compare "days of life sacrificed per year" between profiles.
 * Only renders if 2+ profiles.
 */
export default function ProfileCompareBar({ profiles = [], activeId }) {
  const data = useMemo(
    () =>
      profiles.map((p, i) => ({
        id: p.profile_id,
        name: p.profile_name || `Profil ${i + 1}`,
        jours: p.individual?.jours_vie_sacrifies_par_an || 0,
        ratio: p.individual?.ratio_vie_fiscale_pct || 0,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
      })),
    [profiles]
  );

  if (profiles.length < 2) return null;

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const p = payload[0].payload;
    return (
      <div
        className="rounded-md border border-border bg-card p-3 text-xs shadow-lg"
        data-testid="tooltip-compare"
      >
        <div className="font-semibold" style={{ color: p.color }}>
          {p.name}
        </div>
        <div className="mt-1 flex justify-between gap-4">
          <span className="text-muted-foreground">Jours / an</span>
          <span className="font-mono">{fmtDays(p.jours)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">% vie fiscale</span>
          <span className="font-mono">{fmtPct(p.ratio)}</span>
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="chart-compare">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Comparaison des profils contribuables</span>
          <span className="text-xs font-normal text-muted-foreground">
            jours de vie / an
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-72 w-full" data-testid="chart-compare-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => fmtNumber(v, 0)}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted)/.4)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar name="Jours / an" dataKey="jours" radius={[6, 6, 0, 0]}>
                {data.map((entry) => (
                  <Cell
                    key={entry.id}
                    fill={entry.color}
                    stroke={entry.id === activeId ? PALETTE.gold : "none"}
                    strokeWidth={entry.id === activeId ? 3 : 0}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
